"use client"

import { useEffect, useRef, useState, useSyncExternalStore } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { MapPinIcon } from "@/components/ui/icons"
import { blobMediaUrl } from "@/lib/blob-url"

interface HowItWorksStep {
  number: string
  color: string
  title: string
  description: string
  panel?: React.ReactNode
  ctaLabel?: string
  ctaHref?: string
}

interface HowItWorksScrollProps {
  heading: string
  subtitle: string
  steps: HowItWorksStep[]
  className?: string
}

// px each settled card gets pushed down per card that stacks on top
const STACK_PUSH = 14
// scale reduction per card stacked on top
const SCALE_STEP = 0.03
const MOBILE_BREAKPOINT_QUERY = "(max-width: 767px)"

function getMobileMediaQuery() {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return null
  }
  return window.matchMedia(MOBILE_BREAKPOINT_QUERY)
}

function subscribeToMobileBreakpoint(onStoreChange: () => void) {
  const mediaQuery = getMobileMediaQuery()
  if (!mediaQuery) return () => {}
  mediaQuery.addEventListener("change", onStoreChange)
  return () => mediaQuery.removeEventListener("change", onStoreChange)
}

function getMobileBreakpointSnapshot() {
  return getMobileMediaQuery()?.matches ?? true
}

export function HowItWorksScroll({
  heading,
  subtitle,
  steps,
  className,
}: HowItWorksScrollProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const isMobile = useSyncExternalStore(
    subscribeToMobileBreakpoint,
    getMobileBreakpointSnapshot,
    () => true
  )
  const cardCount = steps.length

  useEffect(() => {
    if (isMobile) return
    const section = sectionRef.current
    if (!section) return

    const onScroll = () => {
      const rect = section.getBoundingClientRect()
      const sectionHeight = section.offsetHeight
      const viewportHeight = window.innerHeight
      // Start progress early — when section top is halfway up the viewport
      const earlyStart = viewportHeight * 0.3
      const scrolled = -rect.top + earlyStart
      const scrollRange = sectionHeight - viewportHeight + earlyStart
      if (scrollRange <= 0) return
      setProgress(Math.max(0, Math.min(1, scrolled / scrollRange)))
    }

    window.addEventListener("scroll", onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener("scroll", onScroll)
  }, [isMobile])

  // Mobile: vertically stacked cards
  if (isMobile) {
    return (
      <section className={cn("py-12", className)}>
        <div className="mb-8 px-4 text-center">
          <h2 className="type-display-md text-neutral-900">{heading}</h2>
          <p className="type-body-lg mt-3 text-neutral-600">{subtitle}</p>
        </div>
        <div className="flex flex-col gap-4 px-4">
          {steps.map((step) => (
            <MobileCard key={step.number} step={step} />
          ))}
        </div>
      </section>
    )
  }

  // Desktop: vertical stacking cards
  // Section height = cardCount+1 viewports; each card gets 1/cardCount of the scroll range
  return (
    <section
      ref={sectionRef}
      className={cn("relative", className)}
      style={{ height: `${(cardCount - 0.85) * 100}vh` }}
    >
      <div
        className="sticky top-0 flex h-dvh flex-col items-center gap-4 overflow-clip pt-12"
        style={{ transform: "translateZ(0)" }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 text-center md:px-6">
          <h2 className="type-display-md text-neutral-900">{heading}</h2>
          <p className="type-body-lg mt-3 text-neutral-600">{subtitle}</p>
        </div>

        {/* Card stack */}
        <div
          className="relative w-full flex-1 px-6"
          style={{ maxWidth: 1038 + 48, maxHeight: 500 }}
        >
          {steps.map((step, i) => {
            // Each card enters during its 1/cardCount slice of progress
            const segStart = i / cardCount
            const localT = Math.max(
              0,
              Math.min(1, (progress - segStart) / (1 / cardCount))
            )
            // Expo-out easing for the slide-in
            const eased = localT < 0.001 ? 0 : 1 - Math.pow(2, -10 * localT)
            // Slide from 100vh → 0 during entry
            const slideVh = progress < segStart ? 100 : (1 - eased) * 100

            // Stack push: as each subsequent card (j > i) enters, it pushes card i down
            let stackPushPx = 0
            let scaleWeight = 0
            for (let j = i + 1; j < cardCount; j++) {
              const jT = Math.max(
                0,
                Math.min(1, (progress - j / cardCount) / (1 / cardCount))
              )
              const jEased = jT < 0.001 ? 0 : 1 - Math.pow(2, -10 * jT)
              stackPushPx += jEased * STACK_PUSH
              scaleWeight += jEased
            }
            const scale = Math.max(0.88, 1 - scaleWeight * SCALE_STEP)

            return (
              <div
                key={step.number}
                style={{
                  position: "absolute",
                  inset: "0",
                  zIndex: i + 1,
                  transform: `translateY(calc(${slideVh}vh + ${stackPushPx}px)) scale(${scale})`,
                  transformOrigin: "top center",
                  willChange: "transform",
                }}
              >
                <DesktopCard step={step} />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function DesktopCard({ step }: { step: HowItWorksStep }) {
  const hasPanel = !!step.panel
  return (
    <div
      className="relative h-full w-full overflow-hidden rounded-[20px]"
      style={{ backgroundColor: step.color }}
    >
      {/* Large step number watermark */}
      <span
        className="pointer-events-none absolute leading-none font-bold text-white/20 select-none"
        style={{
          fontSize: "clamp(200px, 35vw, 500px)",
          right: "2%",
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        {step.number}
      </span>

      {hasPanel ? (
        <div className="absolute inset-0 flex items-center gap-8 px-[6%]">
          {/* Inner mockup panel (left side) */}
          <div className="flex-shrink-0">{step.panel}</div>
          {/* Title + description */}
          <div className="max-w-[380px] pr-8">
            <h3 className="type-display-sm leading-[1.1] text-white">
              {step.title}
            </h3>
            <p className="type-body-lg mt-4 leading-[1.2] text-white">
              {step.description}
            </p>
          </div>
        </div>
      ) : (
        /* No panel — center the content */
        <div className="absolute top-1/2 left-[8%] max-w-[500px] -translate-y-1/2 pr-8">
          <h3 className="type-display-sm leading-[1.1] text-white">
            {step.title}
          </h3>
          <p className="type-body-lg mt-4 leading-[1.2] text-white">
            {step.description}
          </p>
          {step.ctaLabel && step.ctaHref && (
            <a
              href={step.ctaHref}
              className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-white transition-all hover:gap-2"
            >
              {step.ctaLabel} &rarr;
            </a>
          )}
        </div>
      )}
    </div>
  )
}

function MobileCard({ step }: { step: HowItWorksStep }) {
  return (
    <div
      className="relative flex flex-col overflow-hidden rounded-[20px]"
      style={{
        backgroundColor: step.color,
        minHeight: step.panel ? undefined : 260,
      }}
    >
      <div className="relative z-10 p-6 pr-20 pb-0">
        <h3 className="type-headline-lg leading-[1.1] font-normal text-white">
          {step.title}
        </h3>
        <p className="type-body-md mt-3 leading-[1.2] text-white">
          {step.description}
        </p>
        {step.ctaLabel && step.ctaHref && (
          <a
            href={step.ctaHref}
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-white transition-all hover:gap-2"
          >
            {step.ctaLabel} &rarr;
          </a>
        )}
      </div>
      {step.panel && <div className="relative z-10 p-6">{step.panel}</div>}
      {/* Step number — top-right watermark (title block reserves pr-20 for it) */}
      <p className="type-display-lg pointer-events-none absolute top-2 right-4 leading-none font-bold tracking-tighter text-white/20 select-none">
        {step.number}
      </p>
    </div>
  )
}

/* ---------- Reusable glass panel ---------- */

export function GlassPanel({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "rounded-[20px] border border-white/[0.18] bg-white/[0.07]",
        className
      )}
      style={{ padding: 16 }}
    >
      {children}
    </div>
  )
}

/* ---------- Step 1: CRP notification mockup ---------- */

export function Step1Panel({
  hsCrpText,
  actionsText,
}: {
  hsCrpText: string
  actionsText: string
}) {
  return (
    <GlassPanel className="w-full md:w-[340px] md:max-w-[80vw]">
      <div className="mb-4 flex items-center gap-3">
        <Image
          src={blobMediaUrl("/images/misc/hiw-profile.png")}
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 rounded-full object-cover"
        />
        <div className="flex flex-col gap-2">
          <div
            className="rounded-full bg-white/[0.16]"
            style={{ width: 145, height: 7 }}
          />
          <div
            className="rounded-full bg-white/[0.16]"
            style={{ width: 53, height: 7 }}
          />
        </div>
      </div>

      <p className="type-body-md leading-[1.3] text-white/90">{hsCrpText}</p>

      <div className="mt-4 flex items-center gap-3 rounded-[16px] bg-white/[0.07] p-3">
        <Image
          src={blobMediaUrl("/images/misc/hiw-profile.png")}
          alt=""
          width={56}
          height={56}
          className="h-14 w-14 flex-shrink-0 rounded-[12px] object-cover"
        />
        <span className="type-body-md text-white/90">{actionsText}</span>
        <svg
          className="ml-auto flex-shrink-0 text-white/40"
          width="7"
          height="8"
          viewBox="0 0 7 8"
          fill="none"
        >
          <path
            d="M1 1l4 3-4 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </GlassPanel>
  )
}

/* ---------- Step 2: HbA1c chart mockup ---------- */

export function Step2Panel({
  label,
  value,
  unit,
  statusLabel,
  diabetesRange,
  preDiabetesRange,
  normalRange,
}: {
  label: string
  value: string
  unit: string
  statusLabel: string
  diabetesRange: string
  preDiabetesRange: string
  normalRange: string
}) {
  return (
    <div className="flex w-full max-w-[420px] flex-col gap-4 rounded-[18px] border border-white/[0.15] bg-white/[0.08] p-5 backdrop-blur-[10px]">
      {/* Top metric — horizontal on tablet */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-baseline gap-1.5">
          <span className="type-headline-lg leading-none font-medium tracking-tight text-white">
            {value}
          </span>
          <span className="type-label-sm font-mono text-white/70">{unit}</span>
        </div>
        <span className="type-body-md font-medium text-white/90">{label}</span>
        <span className="type-body-sm rounded-full bg-white/[0.12] px-3 py-1 font-semibold text-white">
          {statusLabel}
        </span>
      </div>

      {/* Scale */}
      <div className="relative flex flex-col pr-5">
        {/* Row: Diabetes */}
        <div className="type-label-sm flex h-9 items-center rounded-[10px] px-3 font-medium text-white">
          {diabetesRange}
        </div>

        {/* Row: Pre-diabetes (active) */}
        <div className="type-label-sm flex h-9 items-center rounded-[10px] bg-white/[0.08] px-3 font-medium text-white">
          <span className="flex-shrink-0">{preDiabetesRange}</span>
          <div className="ml-2 flex flex-1 items-center justify-around">
            {[5, 1, -4, 2].map((y, i) => (
              <svg
                key={i}
                width="12"
                height="12"
                viewBox="0 0 14 14"
                fill="white"
                className="flex-shrink-0"
                style={{ transform: `translateY(${y}px)` }}
              >
                <path d="M 8.412 6.809 C 8.412 7.695 7.695 8.412 6.809 8.412 C 5.924 8.412 5.206 7.695 5.206 6.809 C 5.206 5.924 5.924 5.206 6.809 5.206 C 7.695 5.206 8.412 5.924 8.412 6.809 Z M 6.809 11.618 C 9.465 11.618 11.618 9.465 11.618 6.809 C 11.618 4.153 9.465 2 6.809 2 C 4.153 2 2 4.153 2 6.809 C 2 9.465 4.153 11.618 6.809 11.618 Z" />
              </svg>
            ))}
          </div>
        </div>

        {/* Row: Normal */}
        <div className="type-label-sm flex h-9 items-center rounded-[10px] px-3 font-medium text-white">
          {normalRange}
        </div>

        {/* Vertical ruler */}
        <div className="absolute top-0 right-0 bottom-0 flex w-1 flex-col gap-1">
          <div className="flex-1 rounded bg-white" />
          <div className="flex-1 rounded bg-white" />
          <div className="flex-1 rounded bg-white" />
        </div>
      </div>
    </div>
  )
}

/* ---------- Step 3: Fasting glucose gauge mockup ---------- */

export function Step3Panel({
  value,
  unitLabel,
  label,
}: {
  value: string
  unitLabel: string
  label: string
}) {
  /* Segmented circle gauge: 4 segments of a 240° arc with 14° gaps.
     3 olive segments + 1 grey, SVG rotated 150° so the opening faces down. */
  const R = 42
  const C = 2 * Math.PI * R // ≈263.89
  const segDeg = (240 - 14 * 3) / 4 // 49.5°
  const segLen = (segDeg / 360) * C
  const gapLen = (14 / 360) * C
  const colors = ["#c4d172", "#c4d172", "#c4d172", "#e1e1e1"]

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative flex items-center justify-center"
        style={{ width: 180, height: 180 }}
      >
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 h-full w-full"
          style={{ transform: "rotate(150deg)" }}
        >
          {colors.map((color, i) => (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={R}
              fill="none"
              stroke={color}
              strokeWidth="4.8"
              strokeLinecap="round"
              strokeDasharray={`${segLen} ${C}`}
              strokeDashoffset={-i * (segLen + gapLen)}
            />
          ))}
        </svg>
        <div
          className="relative z-10 flex flex-col items-center"
          style={{ transform: "translateY(5px)" }}
        >
          <span className="type-display-lg leading-[0.8] font-normal text-white">
            {value}
          </span>
          <span className="type-body-md mt-3 font-mono text-white uppercase">
            {unitLabel}
          </span>
        </div>
      </div>
      <p
        className="-mt-2 text-center leading-[1.3] text-white"
        style={{ fontSize: 16 }}
      >
        {label}
      </p>
    </div>
  )
}

/* ---------- Step 4: Calendar/follow-up mockup ---------- */

export function Step4Panel({
  dateText,
  timeText,
  confirmedText,
  locationCity,
  locationAddress,
}: {
  dateText: string
  timeText: string
  confirmedText: string
  locationCity: string
  locationAddress: string
}) {
  return (
    <GlassPanel className="w-full md:w-[340px] md:max-w-[80vw]">
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-white/[0.10]">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255,255,255,0.45)"
            strokeWidth="1.5"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <div>
          <p className="type-body-md font-medium text-white">{dateText}</p>
          <p className="type-body-sm text-white/50">{timeText}</p>
        </div>
        <span className="type-label-sm ml-auto rounded-full border border-white/[0.06] bg-white/[0.10] px-2.5 py-1 text-white/70">
          {confirmedText}
        </span>
      </div>

      <div className="mt-1 border-t border-white/[0.08] pt-3">
        <div className="flex items-center gap-2.5">
          <MapPinIcon width={15} height={15} className="text-white/45" />
          <div>
            <p className="type-body-md text-white/90">{locationCity}</p>
            <p className="type-label-sm text-white/40">{locationAddress}</p>
          </div>
        </div>
      </div>
    </GlassPanel>
  )
}
